import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { EnvToggle } from "../OrgUsageAnalytics/components/EnvToggle";
import { useDemoInvitesEnv } from "./useDemoInvitesEnv";
import {
  createDemoInvite,
  extendDemoInvite,
  listDemoInvites,
  regenerateDemoInviteLink,
  resetDemoInviteDevices,
  revokeDemoInvite,
} from "./api";
import "../OrgUsageAnalytics/OrgUsageAnalytics.css";
import "../ReferralEligibility/ReferralEligibility.css";
import "./DemoInvites.css";

const EXPIRY_OPTIONS = [1, 3, 7, 14, 30];
const DEVICE_OPTIONS = [1, 2, 3];
const EMPTY_FORM = {
  email: "",
  firstName: "",
  lastName: "",
  companyName: "",
  note: "",
  expiresInDays: 7,
  maxDevices: 1,
  sendEmail: false,
};

const STATUS_LABELS = {
  pending: "Not opened",
  active: "In use",
  expired: "Expired",
  revoked: "Revoked",
};

function formatTimestamp(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleString();
}

// fetchWithFallback folds the backend body into its error; surface the backend reason when present.
function errorMessage(error) {
  const reason = String(error?.message || "").match(/"reason":"([^"]+)"/)?.[1];

  return reason || error?.message || "Something went wrong. Please try again.";
}

function prospectName(invite) {
  return [invite.firstName, invite.lastName].filter(Boolean).join(" ") || invite.email;
}

function StatusBadge({ status }) {
  return (
    <span className={`ref-badge di-badge-${status}`}>{STATUS_LABELS[status] || status}</span>
  );
}

function LinkResult({ result, onDismiss }) {
  const [isCopied, setIsCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.url);
      setIsCopied(true);
    } catch {
      toast.error("Could not copy. Select the link and copy it manually.");
    }
  };

  return (
    <section className="di-link-result" aria-live="polite">
      <h2 className="ref-section-title">Link for {result.name}</h2>
      <p className="ref-section-subtitle">
        Copy it now: the link is shown only once. To share it again, issue a new link.
        {result.emailSent ? " It was also emailed to the prospect." : ""}
      </p>
      <div className="di-link-row">
        <input className="ref-input di-link-input" readOnly value={result.url} aria-label="Invite link" onFocus={(event) => event.target.select()} />
        <button type="button" className="ref-btn ref-btn-primary" onClick={copy}>
          {isCopied ? "Copied" : "Copy link"}
        </button>
        <button type="button" className="ref-btn ref-btn-ghost" onClick={onDismiss}>
          Done
        </button>
      </div>
    </section>
  );
}

function CreateInvitePanel({ env, onCreated }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const update = (key) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [key]: value }));
    setError(null);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.email.trim() || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await createDemoInvite(env, {
        ...form,
        expiresInDays: Number(form.expiresInDays),
        maxDevices: Number(form.maxDevices),
      });
      if (form.sendEmail && !response.emailSent) {
        toast.error("Invite created, but the email could not be sent. Share the link yourself.");
      }
      onCreated({ url: response.url, emailSent: response.emailSent, name: prospectName(response.invite) });
      setForm(EMPTY_FORM);
    } catch (submitError) {
      setError(errorMessage(submitError));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="ref-add-panel">
      <h2 className="ref-section-title">New invite</h2>
      <p className="ref-section-subtitle">
        The prospect opens the link, confirms this email with a 6-digit code, and the demo locks to their device.
      </p>
      <form className="di-form" onSubmit={submit}>
        <label className="di-field di-field-wide">
          Prospect email (required)
          <input className="ref-input" type="email" required maxLength={254} value={form.email} onChange={update("email")} />
        </label>
        <label className="di-field">
          First name
          <input className="ref-input" maxLength={80} value={form.firstName} onChange={update("firstName")} />
        </label>
        <label className="di-field">
          Last name
          <input className="ref-input" maxLength={80} value={form.lastName} onChange={update("lastName")} />
        </label>
        <label className="di-field">
          Company
          <input className="ref-input" maxLength={80} value={form.companyName} onChange={update("companyName")} />
        </label>
        <label className="di-field">
          Link works for
          <select className="ref-input" value={form.expiresInDays} onChange={update("expiresInDays")}>
            {EXPIRY_OPTIONS.map((days) => (
              <option key={days} value={days}>
                {days} {days === 1 ? "day" : "days"}
              </option>
            ))}
          </select>
        </label>
        <label className="di-field">
          Devices allowed
          <select className="ref-input" value={form.maxDevices} onChange={update("maxDevices")}>
            {DEVICE_OPTIONS.map((count) => (
              <option key={count} value={count}>
                {count}
              </option>
            ))}
          </select>
        </label>
        <label className="di-field di-field-wide">
          Internal note
          <input className="ref-input" maxLength={500} value={form.note} placeholder="Deal, owner or context. Never shown to the prospect." onChange={update("note")} />
        </label>
        <label className="di-checkbox">
          <input type="checkbox" checked={form.sendEmail} onChange={update("sendEmail")} />
          Email the link to the prospect
        </label>
        <div className="di-form-actions">
          <button type="submit" className="ref-btn ref-btn-primary" disabled={isSubmitting || !form.email.trim()}>
            {isSubmitting ? "Creating…" : "Create invite"}
          </button>
        </div>
      </form>
      {error ? <div className="ref-inline-error" role="alert">{error}</div> : null}
    </section>
  );
}

function ConfirmModal({ action, isBusy, onConfirm, onCancel }) {
  const [days, setDays] = useState(7);

  useEffect(() => {
    setDays(7);
  }, [action]);

  if (!action) {
    return null;
  }

  return (
    <div className="ref-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="di-modal-title">
      <div className="ref-modal">
        <h3 id="di-modal-title" className="ref-modal-title">{action.title}</h3>
        <p className="ref-modal-description">{action.description}</p>
        {action.type === "extend" ? (
          <label className="ref-modal-label">
            New expiry, counted from now
            <select className="ref-input" value={days} onChange={(event) => setDays(Number(event.target.value))}>
              {EXPIRY_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option} {option === 1 ? "day" : "days"}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <div className="ref-modal-actions">
          <button type="button" className="ref-btn ref-btn-ghost" onClick={onCancel} disabled={isBusy}>
            Cancel
          </button>
          <button
            type="button"
            className={`ref-btn ${action.tone === "danger" ? "ref-btn-danger" : "ref-btn-primary"}`}
            onClick={() => onConfirm({ days })}
            disabled={isBusy}
          >
            {isBusy ? "Working…" : action.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function buildAction(type, invite) {
  const name = prospectName(invite);
  const actions = {
    revoke: {
      title: `Revoke ${name}'s invite?`,
      description: "The link stops working and any open demo closes within five minutes. This cannot be undone; create a new invite to share again.",
      confirmLabel: "Revoke invite",
      tone: "danger",
    },
    reset: {
      title: `Reset devices for ${name}?`,
      description: "The current device is signed out. The next device to open the link and confirm the email code takes its place.",
      confirmLabel: "Reset devices",
    },
    extend: {
      title: `Extend ${name}'s invite`,
      description: "Pick how long the link keeps working from now. An expired invite opens again.",
      confirmLabel: "Extend",
    },
    regenerate: {
      title: `Issue a new link for ${name}?`,
      description: "The current link stops working and bound devices are released. You will see the new link once.",
      confirmLabel: "Issue new link",
    },
  };

  return { type, invite, ...actions[type] };
}

const DemoInvitesPage = ({ onReady }) => {
  const [env, setEnv] = useDemoInvitesEnv();
  const [invites, setInvites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");
  const [linkResult, setLinkResult] = useState(null);
  const [action, setAction] = useState(null);
  const [isActionBusy, setIsActionBusy] = useState(false);

  useEffect(() => {
    onReady?.();
  }, [onReady]);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await listDemoInvites(env);
      setInvites(response.invites || []);
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setIsLoading(false);
    }
  }, [env]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredInvites = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (!query) {
      return invites;
    }

    return invites.filter((invite) =>
      [invite.email, invite.firstName, invite.lastName, invite.companyName, invite.note]
        .join(" ")
        .toLowerCase()
        .includes(query),
    );
  }, [invites, filter]);

  const replaceInvite = (updated) => {
    setInvites((current) => current.map((invite) => (invite.id === updated.id ? updated : invite)));
  };

  const runAction = async ({ days }) => {
    const { type, invite } = action;
    setIsActionBusy(true);
    try {
      if (type === "revoke") {
        replaceInvite((await revokeDemoInvite(env, invite.id)).invite);
        toast.success("Invite revoked");
      }
      if (type === "reset") {
        replaceInvite((await resetDemoInviteDevices(env, invite.id)).invite);
        toast.success("Devices reset");
      }
      if (type === "extend") {
        replaceInvite((await extendDemoInvite(env, invite.id, days)).invite);
        toast.success("Invite extended");
      }
      if (type === "regenerate") {
        const response = await regenerateDemoInviteLink(env, invite.id);
        replaceInvite(response.invite);
        setLinkResult({ url: response.url, emailSent: false, name: prospectName(response.invite) });
      }
      setAction(null);
    } catch (actionError) {
      toast.error(errorMessage(actionError));
    } finally {
      setIsActionBusy(false);
    }
  };

  return (
    <div className="ref-page">
      <ToastContainer position="top-right" autoClose={3500} />

      <div className="ref-page-header">
        <div>
          <p className="ref-eyebrow">Sales</p>
          <h1 className="ref-page-title">Demo Invites</h1>
          <p className="ref-page-subtitle">
            Private links to the self-serve demo. Each link works for one email, a limited number of devices and a set time.
          </p>
        </div>
        <div className="ref-header-controls">
          <EnvToggle env={env} onChange={setEnv} />
          <button type="button" className="ref-btn ref-btn-ghost" onClick={load} disabled={isLoading}>
            Refresh
          </button>
        </div>
      </div>

      {env !== "dev" ? (
        <p className="ref-preview-note">
          The live demo app reads invites from Dev. Invites created in {env === "prod" ? "Prod" : "Local"} will not open in it.
        </p>
      ) : null}

      {linkResult ? <LinkResult key={linkResult.url} result={linkResult} onDismiss={() => setLinkResult(null)} /> : null}

      <CreateInvitePanel
        env={env}
        onCreated={(result) => {
          setLinkResult(result);
          load();
        }}
      />

      <div className="ref-table-controls">
        <input
          className="ref-input ref-filter"
          placeholder="Filter by name, email, company or note"
          aria-label="Filter invites"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
        />
        <span className="ref-muted">
          {filteredInvites.length} of {invites.length} invites
        </span>
      </div>

      {error ? <div className="ref-error" role="alert">{error}</div> : null}

      {isLoading ? (
        <div className="ref-empty">Loading demo invites…</div>
      ) : (
        <div className="ref-table-wrapper">
          {filteredInvites.length === 0 ? (
            <div className="ref-empty">{invites.length ? "No invites match your filter." : "No invites yet. Create one above."}</div>
          ) : (
            <table className="ref-table">
              <thead>
                <tr>
                  <th>Prospect</th>
                  <th>Status</th>
                  <th>Devices</th>
                  <th>Last seen</th>
                  <th>Expires</th>
                  <th>Blocked tries</th>
                  <th className="ref-actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvites.map((invite) => {
                  const isClosed = invite.status === "revoked";

                  return (
                    <tr key={invite.id}>
                      <td>
                        <div className="ref-org-name">{prospectName(invite)}</div>
                        <div className="ref-muted ref-org-id">
                          {invite.email}
                          {invite.companyName ? ` · ${invite.companyName}` : ""}
                        </div>
                        {invite.note ? <div className="ref-muted di-note">{invite.note}</div> : null}
                      </td>
                      <td>
                        <StatusBadge status={invite.status} />
                      </td>
                      <td>
                        {invite.devices.length} of {invite.maxDevices}
                        {invite.devices[0]?.userAgent ? (
                          <div className="ref-muted di-note" title={invite.devices[0].userAgent}>
                            {invite.devices[0].userAgent}
                          </div>
                        ) : null}
                      </td>
                      <td>{formatTimestamp(invite.lastSeenAt)}</td>
                      <td>{formatTimestamp(invite.expiresAt)}</td>
                      <td>
                        {invite.blockedDeviceAttempts || 0}
                        {invite.lastBlockedAt ? (
                          <div className="ref-muted di-note">Last {formatTimestamp(invite.lastBlockedAt)}</div>
                        ) : null}
                      </td>
                      <td className="ref-actions-col">
                        {isClosed ? (
                          <span className="ref-muted">—</span>
                        ) : (
                          <div className="ref-row-actions di-row-actions">
                            <button type="button" className="ref-btn ref-btn-ghost ref-btn-sm" onClick={() => setAction(buildAction("extend", invite))}>
                              Extend
                            </button>
                            <button
                              type="button"
                              className="ref-btn ref-btn-ghost ref-btn-sm"
                              disabled={!invite.devices.length}
                              onClick={() => setAction(buildAction("reset", invite))}
                            >
                              Reset devices
                            </button>
                            <button
                              type="button"
                              className="ref-btn ref-btn-ghost ref-btn-sm"
                              disabled={invite.status === "expired"}
                              title={invite.status === "expired" ? "Extend the invite first" : undefined}
                              onClick={() => setAction(buildAction("regenerate", invite))}
                            >
                              New link
                            </button>
                            <button type="button" className="ref-btn ref-btn-danger ref-btn-sm" onClick={() => setAction(buildAction("revoke", invite))}>
                              Revoke
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      <ConfirmModal action={action} isBusy={isActionBusy} onConfirm={runAction} onCancel={() => setAction(null)} />
    </div>
  );
};

export default DemoInvitesPage;
